<template>
  <div class="client-overview">
    <WdsStack gap-lg class="client-overview__columns tw-w-full">
      <WdsStack class="client-overview__main tw-w-3/5 tw-min-w-0" direction-column gap-lg>
        <ClientRelationCard :client="relation" :loading="loading" />
        <ClientDetailsCard :client="relation" :loading="loading" />
        <ClientCustomFields
          v-if="!agencyLink"
          :key="clientRelationId"
          :company-supplier-id="clientRelationId"
        />
      </WdsStack>

      <WdsStack class="client-overview__aside tw-h-max tw-w-2/5 tw-min-w-0" direction-column gap-lg>
        <ClientPaymentsCard v-if="!agencyLink" :client="client" :loading="loading" />
        <!-- A direct agency link has nobody on the other side to show. -->
        <ClientContactsCard
          v-if="!agencyLink?.isDirect"
          :client="relation"
          :loading="loading"
          @view-people="emit('viewPeople')"
        />
      </WdsStack>
    </WdsStack>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { WdsStack } from '@worksome/wds-components'
import { graphql } from '@/gql'
import ClientRelationCard from '@/components/Clients/OverviewCards/ClientRelationCard.vue'
import ClientDetailsCard from '@/components/Clients/OverviewCards/ClientDetailsCard.vue'
import ClientPaymentsCard from '@/components/Clients/OverviewCards/ClientPaymentsCard.vue'
import ClientContactsCard from '@/components/Clients/OverviewCards/ClientContactsCard.vue'
import ClientCustomFields from '@/components/Clients/ClientCustomFields.vue'

const props = defineProps<{ clientRelationId: string }>()

const emit = defineEmits<{ (e: 'viewPeople'): void }>()

// One request for the tab; each card reads its own fragment off the shared result.
const { result, loading } = useQuery(
  graphql(`
    query ClientOverviewData($id: ID!) {
      clientRelation(id: $id) {
        id
        ... on SupplierClient {
          isDirect
        }
        ...ClientRelationCard
        ...ClientDetailsCard
        ...ClientPaymentsCard
        ...ClientContactsCard
      }
    }
  `),
  () => ({ id: props.clientRelationId }),
  { clientId: 'publicApi' },
)

const relation = computed(() => result.value?.clientRelation ?? null)

const client = computed(() =>
  relation.value?.__typename === 'CompanySupplier' ? relation.value : null,
)

const agencyLink = computed(() =>
  relation.value?.__typename === 'SupplierClient' ? relation.value : null,
)
</script>

<style scoped lang="scss">
// Sized by the container, not the viewport: the overview renders standalone and inside the overlay.
.client-overview {
  container-type: inline-size;
}

@container (max-width: 899px) {
  .client-overview__columns {
    flex-direction: column;
  }

  .client-overview__main,
  .client-overview__aside {
    width: 100%;
  }
}
</style>
